import { memo, type ReactNode, useCallback } from 'react';

import { useAnalyticsUtilities } from '@air/analytics';
import { ZapIcon } from '@air/design-icons';
import { Popover, type PopoverProps } from '@air/primitive-popover';
import { useAirModal } from '@air/provider-modal';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export type UpsellPopoverProps = {
  /** The upsell copy (e.g. SHARE_MODAL_UPSELL_MESSAGE); "Click to upgrade." is appended to it. */
  children: ReactNode;
  /** The element that opens the popover — the flash button, or a whole control that is itself locked. */
  trigger: PopoverProps['trigger'];
};

/**
 * The plan-gate upsell message, and the Plans modal it opens, hung off a caller-supplied trigger.
 *
 * Two shapes of trigger exist: the flash icon beside a locked control (UpsellFlashIconButton), and a
 * locked control that carries the flash INSIDE itself and opens the upsell as a whole
 * (ShareDesignImageButton, EnrichmentUpsellButton). The message, the analytics event and the upgrade
 * path are the same either way, so they live here rather than in each trigger.
 */
export const UpsellPopover = memo(({ children, trigger }: UpsellPopoverProps) => {
  const [showPlansModal] = useAirModal(PlansModal);
  const { track } = useAnalyticsUtilities();
  const { data: subscription } = useSubscriptionContext();

  const handleUpgradeClick = useCallback(() => {
    // Track upgrade button clicked with current plan
    track('Upgrade Button Clicked', {
      plan: subscription?.plan || 'unknown',
    });

    showPlansModal();
  }, [track, subscription?.plan, showPlansModal]);

  return (
    <Popover align="start" className="max-w-80 border-0" data-theme="dark" side="top" trigger={trigger}>
      <div className="flex items-center gap-3 p-2">
        <ZapIcon aria-hidden className="ads:text-icon-primary text-blue-9" size="xl" />
        <div className="ads:text-primary text-12 text-gray-12">
          {children}{' '}
          <button
            className="cursor-pointer ads:text-primary text-gray-12 underline ads:hover:text-secondary hover:text-gray-11"
            onClick={handleUpgradeClick}
            type="button"
          >
            Click to upgrade.
          </button>
        </div>
      </div>
    </Popover>
  );
});

UpsellPopover.displayName = 'UpsellPopover';
